import { useEffect, useMemo, useRef, useState } from "react";
import { BellRing } from "lucide-react";
import { DayCard } from "./DayCard";
import { DiaryIntro } from "./DiaryIntro";
import { DiaryDone, DiaryFinish } from "./DiaryFinish";
import { SaveStatus } from "./SaveStatus";
import { pendingFollowups, plural } from "./diaryUtils";

const detail = (e) => e?.response?.data?.detail;

// Debounced autosave that only fires when the diary actually changed since the last successful save.
function useDiarySave(adapter, diary, enabled) {
  const [s, setS] = useState({ status: "idle", error: null, closed: false });
  const [nonce, setNonce] = useState(0);
  const last = useRef(JSON.stringify(diary));
  useEffect(() => {
    const json = JSON.stringify(diary);
    if (!enabled || !adapter.saveDraft || json === last.current) return undefined;
    setS((p) => ({ ...p, status: "saving" }));
    const t = setTimeout(() => {
      adapter.saveDraft(diary)
        .then(() => { last.current = json; setS({ status: "saved", error: null, closed: false }); })
        .catch((e) => {
          const code = e?.response?.status;
          const d = detail(e);
          setS({ status: "error", closed: code === 409 || code === 410,
            error: typeof d === "string" ? d : "We couldn't save your last change. Check your connection and try again." });
        });
    }, 700);
    return () => clearTimeout(t);
  }, [adapter, diary, enabled, nonce]);
  return { ...s, retry: () => setNonce((n) => n + 1) };
}

/** Client-facing 7-day diary: one private link, entries editable until the diary is sent. */
export function DiaryView({ data, adapter }) {
  const [diary, setDiary] = useState(data.diary || { days: {} });
  const [result, setResult] = useState(null);
  const t = data.template;
  const save = useDiarySave(adapter, diary, !result);
  const pending = useMemo(() => pendingFollowups(diary, data.calendar, t.followup_fields), [diary, data.calendar, t.followup_fields]);
  const pendingDays = new Set(pending.map((p) => p.day.n));

  if (result) return <DiaryDone result={result} copy={t.copy} />;
  return (
    <div className="space-y-8" data-testid="diary-page">
      <DiaryIntro data={data} adapter={adapter} />
      {pending.length > 0 && (
        <div className="flex items-start gap-3 rounded-2xl border-2 border-sky-300 bg-sky-50 p-4 text-base text-sky-950" data-testid="diary-pending-followups">
          <BellRing className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p><strong>{plural(pending.length, t.entry_label)}</strong> from earlier days {pending.length === 1 ? "has" : "have"} a quick “how did you recover?” question waiting. They&apos;re open below.</p>
        </div>
      )}
      <div className="space-y-3" data-testid="diary-days">
        {data.calendar.map((d) => (
          <DayCard key={d.n} day={d} isToday={d.date === data.today} defaultOpen={d.date === data.today || pendingDays.has(d.n)}
                   template={t} diary={diary} onDiary={setDiary} readOnly={save.closed} />
        ))}
      </div>
      <DiaryFinish data={data} diary={diary} pending={pending.length} adapter={adapter} onDone={setResult} disabled={save.closed} />
      {t.copy?.disclaimer && <p className="text-sm text-zinc-500" data-testid="diary-disclaimer">{t.copy.disclaimer}</p>}
      <SaveStatus save={save} />
    </div>
  );
}
